/**
 * English: Inventory of agent tools — MCP tools registered to FastMCP + LangGraph node capabilities.
 * Thai: หน้าแสดงรายการเครื่องมือของ agent — MCP tools ที่ลงทะเบียนกับ FastMCP + ความสามารถของ node ใน LangGraph
 *
 * Data is hardcoded because tools change via code only (not runtime config).
 * Source of truth: agent-station services and MCP servers
 */
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CubeTransparentIcon,
  CalculatorIcon,
  ClockIcon,
  ChartBarSquareIcon,
  HeartIcon,
  CommandLineIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import PageShell from '../components/layout/PageShell';
import PageHeader from '../components/layout/PageHeader';

type IconT = ComponentType<SVGProps<SVGSVGElement>>;

interface ToolEntry {
  name: string;
  summary: string;
  args?: string;
}

interface ToolGroup {
  title: string;
  subtitle: string;
  icon: IconT;
  source: string;
  tools: ToolEntry[];
}

const MCP_GROUPS: ToolGroup[] = [
  {
    title: 'Device KPI',
    subtitle: 'PostgreSQL-backed device roster and status',
    icon: CubeTransparentIcon,
    source: 'agent-station/mcp-servers + inventory-service',
    tools: [
      { name: 'get_summary_stats', summary: 'สรุปข้อมูลอุปกรณ์ทั้ง station (legacy alias)', args: 'station_id, top_n=3' },
      { name: 'get_device_stats', summary: 'สถิติ + breakdown ตามประเภท + Top/Bottom N', args: 'station_id, top_n=3' },
      { name: 'get_device_detail', summary: 'รายละเอียดอุปกรณ์เครื่องเดียว (ทุกฟิลด์)', args: 'station_id, device_id' },
      { name: 'get_devices_by_status', summary: 'กรอง devices ตาม device_status (start/stop/unknown)', args: 'station_id, status, org_name?' },
      { name: 'get_all_station_devices', summary: 'คืน devices ทั้งหมดของ station เป็น JSON', args: 'station_id, org_name?' },
    ],
  },
  {
    title: 'Math',
    subtitle: 'Safe arithmetic evaluation (no eval)',
    icon: CalculatorIcon,
    source: 'agent-station/mcp-servers',
    tools: [
      { name: 'calculate', summary: 'คำนวณนิพจน์คณิตศาสตร์ปลอดภัย (+ − × ÷ // % **) ผ่าน ast', args: 'expression' },
    ],
  },
  {
    title: 'Time',
    subtitle: 'Clock + IANA timezone (stdlib zoneinfo)',
    icon: ClockIcon,
    source: 'agent-station/mcp-servers',
    tools: [
      { name: 'get_current_time', summary: 'เวลาปัจจุบัน UTC/local + วันในสัปดาห์ (ไทย/อังกฤษ)', args: 'timezone?' },
      { name: 'convert_to_timezone', summary: 'แปลง ISO-8601 ไปยัง timezone ปลายทาง', args: 'iso_datetime, target_timezone' },
    ],
  },
  {
    title: 'Statistics',
    subtitle: 'Descriptive stats + hypothesis tests (stdlib only)',
    icon: ChartBarSquareIcon,
    source: 'agent-station/mcp-servers',
    tools: [
      { name: 'summarize_statistics', summary: 'mean/median/quartiles/IQR/variance/skew/kurtosis', args: 'numbers_json' },
      { name: 'independent_samples_ttest', summary: 'Welch t-test (ค่าเริ่มต้น) หรือ Student pooled t-test', args: 'sample_a_json, sample_b_json, welch=true' },
      { name: 'paired_samples_ttest', summary: 'Paired t-test บนผลต่าง before/after', args: 'pairs_json' },
      { name: 'one_way_anova_f_ratio', summary: 'One-way ANOVA F-ratio (2+ groups)', args: 'groups_json' },
      { name: 'pearson_correlation', summary: 'Pearson r + r² + t (df=n−2)', args: 'x_json, y_json' },
      { name: 'spearman_correlation', summary: 'Spearman ρ (non-parametric, rank-based)', args: 'x_json, y_json' },
      { name: 'simple_linear_regression', summary: 'OLS y = a·x + b + R² + SE(slope/intercept)', args: 'x_json, y_json' },
      { name: 'mann_whitney_u_test', summary: 'U test (non-parametric two-sample)', args: 'sample_a_json, sample_b_json' },
      { name: 'kruskal_wallis_test', summary: 'H test (non-parametric ANOVA alternative)', args: 'groups_json' },
      { name: 'wilcoxon_signed_rank_test', summary: 'Signed-rank W (non-parametric paired)', args: 'pairs_json' },
      { name: 'chi_square_goodness_of_fit', summary: 'χ² observed vs expected frequencies', args: 'observed_json, expected_json' },
      { name: 'chi_square_independence', summary: 'χ² contingency table + Cramér\'s V', args: 'table_json' },
      { name: 'one_proportion_z_test', summary: 'Proportion z-test vs p₀', args: 'count, n, p0' },
      { name: 'two_proportion_z_test', summary: 'Two-sample proportion z-test (pooled SE)', args: 'count_a, n_a, count_b, n_b' },
      { name: 'z_scores', summary: 'Sample z-scores ของทุก element', args: 'numbers_json' },
      { name: 'detect_outliers', summary: 'ค้น outliers แบบ IQR (1.5×) และ |z|>3', args: 'numbers_json' },
    ],
  },
  {
    title: 'System Health',
    subtitle: 'Backend readiness probes',
    icon: HeartIcon,
    source: 'agent-station/mcp-servers',
    tools: [
      { name: 'get_system_health', summary: 'ตรวจสถานะ PostgreSQL + Redis + MCP client', args: '(no args)' },
    ],
  },
];

// 16 commands registered in device-services/control-service
const DEVICE_COMMANDS: string[] = [
  'start', 'stop', 'maintenance', 'active',
  'automanual', 'submode', 'period', 'schedule',
  'set_cooldown', 'target_set', 'area_settings', 'ball_disable',
  'refresh', 'reset_overload', 'reset_cooldown', 'reset_total_runtime',
];

interface CapabilityEntry {
  title: string;
  subtitle: string;
  icon: IconT;
  source: string;
  body: React.ReactNode;
}

const CAPABILITIES: CapabilityEntry[] = [
  {
    title: 'Device Control (16 commands)',
    subtitle: 'Go Fiber service ส่งคำสั่งไปยัง Factory API ด้วย Bearer JWT',
    icon: CommandLineIcon,
    source: 'device-services/control-service',
    body: (
      <div className="flex flex-wrap gap-1.5">
        {DEVICE_COMMANDS.map((cmd) => (
          <code
            key={cmd}
            className="px-2 py-0.5 rounded text-[11px] font-mono bg-app-muted text-app-text-sec border border-app-border"
          >
            {cmd}
          </code>
        ))}
      </div>
    ),
  },
  {
    title: 'Ask (unified)',
    subtitle: 'ถาม · คุย · ข้อมูลเชิงลึก — endpoint เดียว',
    icon: ChatBubbleLeftRightIcon,
    source: 'web-admin/services/chat-api-service + agent-station/services/agent-chat · POST /api/v2/chat',
    body: (
      <p className="text-xs text-app-text-sec leading-relaxed">
        ใช้ <code className="text-[11px] bg-app-muted px-1 rounded">POST /api/v2/chat</code> พร้อม{' '}
        <code className="text-[11px] bg-app-muted px-1 rounded">mode: ask</code> (หรือไม่ส่ง mode — server infer เป็น ask).
        มี <code className="text-[11px] bg-app-muted px-1 rounded">station_id</code> → กราฟ Manager; ไม่มี → direct LLM
      </p>
    ),
  },
  {
    title: 'Manager / Master Routing',
    subtitle: 'LangGraph fan-out ไปยัง device agents ในสถานี หรือข้ามสถานี',
    icon: UserGroupIcon,
    source: 'agent-station/services/agent-chat',
    body: (
      <ul className="space-y-1.5 text-xs text-app-text-sec">
        <li>
          <strong className="text-app-text">Manager (per-station)</strong> —{' '}
          <code className="text-[11px] bg-app-muted px-1 rounded">POST /api/v2/chat</code> (mode=ask) → manager_node → fan-out
          device contribution → discussion → synthesize → optional confirm/execute
        </li>
        <li>
          <strong className="text-app-text">Master (cross-station)</strong> —{' '}
          <code className="text-[11px] bg-app-muted px-1 rounded">POST /api/v2/chat/master</code> → master_node เลือก stations
          อัตโนมัติ (JSON <code className="text-[11px]">target_stations</code>) → fan-out manager_&#123;station&#125;_node
        </li>
        <li>
          <strong className="text-app-text">Device-direct</strong> —{' '}
          <code className="text-[11px] bg-app-muted px-1 rounded">POST /api/v2/chat/employee</code> → คุยตรงกับ device agent
          เดียว (agent_id = <code className="text-[11px]">device_&#123;org&#125;_&#123;id&#125;</code>) ข้าม Manager
        </li>
      </ul>
    ),
  },
  {
    title: 'Confirm / Execute (JWT gate)',
    subtitle: 'Safety gate สำหรับ control mode — ใช้ Bearer JWT ไม่ใช้ OTP แล้ว',
    icon: ShieldCheckIcon,
    source: 'device-services/control-service + agent-station/services/agent-chat',
    body: (
      <p className="text-xs text-app-text-sec leading-relaxed">
        เมื่อ intent เป็น control family: <code className="text-[11px] bg-app-muted px-1 rounded">confirm_node</code> สกัด intent
        → <code className="text-[11px] bg-app-muted px-1 rounded">confirm_execute_node</code> เรียก{' '}
        <code className="text-[11px] bg-app-muted px-1 rounded">execute_device_command()</code> พร้อม Bearer JWT ของผู้ใช้ —
        legacy <code className="text-[11px]">/api/confirm/*</code> OTP endpoints คืน HTTP 410
      </p>
    ),
  },
];

function ToolCard({ entry }: { entry: ToolEntry }) {
  return (
    <div className="rounded-lg border border-app-border/60 bg-app-bg/60 px-3 py-2.5 hover:border-app-border transition-colors">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <code className="text-[13px] font-mono font-semibold text-app-text">{entry.name}</code>
        {entry.args && (
          <span className="text-[10px] font-mono text-app-text-dim">{entry.args}</span>
        )}
      </div>
      <p className="mt-1 text-xs text-app-text-sec leading-relaxed">{entry.summary}</p>
    </div>
  );
}

function GroupCard({ group }: { group: ToolGroup }) {
  const Icon = group.icon;
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-app-accent/10 text-app-accent flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-app-text">{group.title}</h3>
          <p className="text-xs text-app-text-dim mt-0.5">{group.subtitle}</p>
          <p className="text-[10px] font-mono text-app-text-dim/80 mt-1 break-all">{group.source}</p>
        </div>
        <span className="ml-auto shrink-0 text-[11px] font-medium text-app-text-dim bg-app-muted px-2 py-0.5 rounded-full tabular-nums">
          {group.tools.length} {group.tools.length === 1 ? 'tool' : 'tools'}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {group.tools.map((t) => (
          <ToolCard key={t.name} entry={t} />
        ))}
      </div>
    </div>
  );
}

function CapabilityCard({ entry }: { entry: CapabilityEntry }) {
  const Icon = entry.icon;
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-app-master/10 text-app-master flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-app-text">{entry.title}</h3>
          <p className="text-xs text-app-text-dim mt-0.5">{entry.subtitle}</p>
          <p className="text-[10px] font-mono text-app-text-dim/80 mt-1 break-all">{entry.source}</p>
        </div>
      </div>
      {entry.body}
    </div>
  );
}

export default function ToolsPage() {
  const { t } = useTranslation();
  const totalMcpTools = MCP_GROUPS.reduce((sum, g) => sum + g.tools.length, 0);

  return (
    <PageShell>
      <PageHeader
        title={t('tools.pageTitle')}
        description={t('tools.pageDescription', { mcp: totalMcpTools, cap: CAPABILITIES.length })}
      />

      {/* Section 1: MCP Tools */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 border-b border-app-border pb-2">
          <CubeTransparentIcon className="w-5 h-5 text-app-accent" />
          <h2 className="text-base font-semibold text-app-text">{t('tools.mcpTools')}</h2>
          <span className="text-xs text-app-text-dim">{t('tools.mcpHint')}</span>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {MCP_GROUPS.map((g) => (
            <GroupCard key={g.title} group={g} />
          ))}
        </div>
      </section>

      {/* Section 2: LangGraph Capabilities */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 border-b border-app-border pb-2">
          <UserGroupIcon className="w-5 h-5 text-app-master" />
          <h2 className="text-base font-semibold text-app-text">{t('tools.capabilities')}</h2>
          <span className="text-xs text-app-text-dim">{t('tools.capHint')}</span>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {CAPABILITIES.map((c) => (
            <CapabilityCard key={c.title} entry={c} />
          ))}
        </div>
      </section>

      {/* Footer note */}
      <div className="text-[11px] text-app-text-dim bg-app-muted/60 border border-app-border rounded-lg px-4 py-2.5">
        หน้านี้เป็น static reference — หากเพิ่ม/ลด MCP tool หรือ LangGraph node ต้องอัปเดตข้อมูลในไฟล์{' '}
        <code className="text-[10px] bg-app-bg px-1 rounded">frontend-admin/src/pages/ToolsPage.tsx</code> ให้ตรงกับ backend
      </div>
    </PageShell>
  );
}

